/**
 * Shared engagement scoring for Learn Discover / Browse.
 *
 * TODO: Personalize `computeContentEngagementScore` using the signed-in learner
 * (history, role, completions) instead of a single org-wide ranking.
 */

/** Application-defined weights (tune centrally). */
export const CONTENT_ENGAGEMENT_WEIGHTS = {
	/** Lower than likes/saves — breadth of starts is a weaker ranking signal than explicit engagement. */
	learners: 0.85,
	likes: 1.5,
	saves: 1.65,
	comments: 1.45,
	shares: 1.35,
	/** Signed quality signal: 3★ is neutral; above uplifts, below penalizes — weighted above raw likes/saves. */
	ratingAverage: 5.75,
	/** Confidence from number of ratings (only when avg ≠ 3); higher = strong averages with volume matter more. */
	ratingCount: 1.15,
} as const;

export interface ContentEngagementScoreInput {
	learnersStartedCount: number;
	likeCount: number;
	saveCount: number;
	commentCount: number;
	shareCount: number;
	ratingAverage: number;
	ratingCount: number;
}

export interface EngagementWindow {
	/** Inclusive lower bound; omit for “all time” lower edge (epoch). */
	from?: Date;
	/** Exclusive or inclusive upper bound; omit for “now”. */
	to?: Date;
}

/**
 * All-time / aggregate-based score (uses denormalized counters on tiles +
 * aggregates, not raw events).
 */
export function computeContentEngagementScore(
	input: ContentEngagementScoreInput,
): number {
	const L = Math.max(0, Math.floor(input.learnersStartedCount));
	const likes = Math.max(0, input.likeCount);
	const saves = Math.max(0, input.saveCount);
	const comments = Math.max(0, input.commentCount);
	const shares = Math.max(0, input.shareCount);
	const avg = Math.max(0, Math.min(5, Number(input.ratingAverage) || 0));
	const rc = Math.max(0, Math.floor(input.ratingCount));
	const delta = avg - 3; // baseline neutral at 3★
	const normalizedDelta = delta >= 0 ? delta / 2 : delta / 3; // map to ~[-1, +1]
	const confidence = Math.log1p(rc);
	const ratingSignal =
		normalizedDelta === 0
			? 0
			: CONTENT_ENGAGEMENT_WEIGHTS.ratingAverage *
				normalizedDelta *
				confidence;
	const ratingConfidence =
		normalizedDelta === 0
			? 0
			: CONTENT_ENGAGEMENT_WEIGHTS.ratingCount *
				Math.abs(normalizedDelta) *
				confidence;
	return (
		CONTENT_ENGAGEMENT_WEIGHTS.learners * Math.log1p(L) +
		CONTENT_ENGAGEMENT_WEIGHTS.likes * likes +
		CONTENT_ENGAGEMENT_WEIGHTS.saves * saves +
		CONTENT_ENGAGEMENT_WEIGHTS.comments * comments +
		CONTENT_ENGAGEMENT_WEIGHTS.shares * shares +
		ratingSignal +
		ratingConfidence
	);
}

/**
 * Score from engagement **events** inside `[from, to]` (likes/saves/comments/shares).
 * When `allTime` is passed, rating + learner log terms are blended in so items
 * are not ordered only on thin short-window traffic.
 */
export function computeContentEngagementScoreForWindow(params: {
	window: Pick<
		ContentEngagementScoreInput,
		"likeCount" | "saveCount" | "commentCount" | "shareCount"
	>;
	allTime?: ContentEngagementScoreInput;
	/** 0 = window-only social; 1 = full all-time auxiliary signal. Default 0.35. */
	allTimeAuxiliaryBlend?: number;
}): number {
	const blend = params.allTimeAuxiliaryBlend ?? 0.35;
	const w = params.window;
	let s =
		CONTENT_ENGAGEMENT_WEIGHTS.likes * Math.max(0, w.likeCount) +
		CONTENT_ENGAGEMENT_WEIGHTS.saves * Math.max(0, w.saveCount) +
		CONTENT_ENGAGEMENT_WEIGHTS.comments * Math.max(0, w.commentCount) +
		CONTENT_ENGAGEMENT_WEIGHTS.shares * Math.max(0, w.shareCount);
	if (params.allTime && blend > 0) {
		s += blend * computeContentEngagementScore(params.allTime);
	}
	return s;
}

/** Weights for how much each published entity type contributes per skill. */
export const SKILL_PUBLICATION_WEIGHTS = {
	topic: 1,
	module: 2.2,
	course: 3.4,
} as const;

export interface SkillEngagementPublicationInput {
	topicCount: number;
	moduleCount: number;
	courseCount: number;
	/** Sum of learnersStartedCount across linked published topics/modules/courses. */
	linkedLearnersStartedSum: number;
}

/**
 * Browse “Skills” ranking: breadth of published catalog using that skill,
 * amplified by aggregate learner starts on those rows.
 *
 * TODO: incorporate per-entity completion velocity / quality signals when available.
 */
export function computeSkillBrowseEngagementScore(
	input: SkillEngagementPublicationInput,
): number {
	const t = Math.max(0, Math.floor(input.topicCount));
	const m = Math.max(0, Math.floor(input.moduleCount));
	const c = Math.max(0, Math.floor(input.courseCount));
	const L = Math.max(0, Math.floor(input.linkedLearnersStartedSum));
	const breadth =
		SKILL_PUBLICATION_WEIGHTS.topic * t +
		SKILL_PUBLICATION_WEIGHTS.module * m +
		SKILL_PUBLICATION_WEIGHTS.course * c;
	return breadth * (1 + 0.08 * Math.log1p(L));
}
